import React from 'react';

interface FinancialData {
  revenue_2019: number;
  revenue_2020: number;
  revenue_2021: number;
  revenue_2022: number;
  variable_profit_2019?: number;
  variable_profit_2020?: number;
  variable_profit_2021?: number;
  variable_profit_2022?: number;
  monthly_data?: Array<{
    month: string;
    revenue: number;
    variable_profit: number;
  }>;
}

interface Props {
  data: FinancialData;
}

export default function FinancialSummary({ data }: Props) {
  const formatCurrency = (val: number | undefined) => {
    if (val === undefined || val === null || isNaN(val)) return '$0.00';
    return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const revenueData = [
    { year: '2019', amount: data.revenue_2019 || 0, vp: data.variable_profit_2019 || (data.revenue_2019 || 0) * 0.32 },
    { year: '2020', amount: data.revenue_2020 || 0, vp: data.variable_profit_2020 || (data.revenue_2020 || 0) * 0.28 },
    { year: '2021', amount: data.revenue_2021 || 0, vp: data.variable_profit_2021 || (data.revenue_2021 || 0) * 0.31 },
    { year: '2022', amount: data.revenue_2022 || 0, vp: data.variable_profit_2022 || (data.revenue_2022 || 0) * 0.30 }
  ];

  const maxRevenue = Math.max(...revenueData.map(d => d.amount));
  const maxVP = Math.max(...revenueData.map(d => d.vp));
  const scale = maxRevenue > 0 ? 100 / maxRevenue : 1;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">4-Year Financial Summary</h3>
      
      {/* Revenue Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2 px-3 font-semibold text-gray-700">Year</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-700">Revenue</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-700">Variable Profit</th>
              <th className="text-right py-2 px-3 font-semibold text-gray-700">VP %</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.map((row, idx) => {
              const vpPercent = ((row.vp / row.amount) * 100).toFixed(1);
              const isDown = idx > 0 && row.amount < revenueData[idx - 1].amount;
              return (
                <tr key={row.year} className="border-b border-gray-100">
                  <td className="py-2 px-3 font-medium text-gray-900">{row.year}</td>
                  <td className={`text-right py-2 px-3 font-semibold ${isDown ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCurrency(row.amount)}
                  </td>
                  <td className="text-right py-2 px-3 text-gray-700">
                    {formatCurrency(Math.round(row.vp))}
                  </td>
                  <td className="text-right py-2 px-3 text-gray-600">
                    {vpPercent}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Visual Chart */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue vs Variable Profit Trend</h4>
        {revenueData.map((row) => (
          <div key={row.year} className="space-y-1">
            <div className="flex justify-between text-xs font-medium text-gray-600">
              <span>{row.year}</span>
              <span>{formatCurrency(row.amount)}</span>
            </div>
            <div className="relative h-8 bg-gray-100 rounded">
              {/* Revenue bar */}
              <div
                className="absolute top-0 left-0 h-4 bg-blue-500 rounded"
                style={{ width: `${row.amount * scale}%` }}
                title={`Revenue: ${formatCurrency(row.amount)}`}
              />
              {/* Variable Profit bar */}
              <div
                className="absolute bottom-0 left-0 h-4 bg-green-500 rounded"
                style={{ width: `${row.vp * scale}%` }}
                title={`Variable Profit: ${formatCurrency(Math.round(row.vp))}`}
              />
            </div>
          </div>
        ))}
        <div className="flex gap-4 text-xs mt-3">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-gray-600">Revenue</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span className="text-gray-600">Variable Profit</span>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Insights</h4>
        <ul className="space-y-1 text-sm text-gray-600">
          <li>• 2020 Revenue Impact: <span className="font-semibold text-red-600">-{formatCurrency((data.revenue_2019 || 0) - (data.revenue_2020 || 0))}</span> (-{((((data.revenue_2019 || 0) - (data.revenue_2020 || 0)) / (data.revenue_2019 || 1)) * 100).toFixed(1)}%)</li>
          <li>• Recovery by 2021: {formatCurrency(data.revenue_2021 || 0)} ({(((data.revenue_2021 || 0) / (data.revenue_2019 || 1)) * 100).toFixed(1)}% of 2019)</li>
          <li>• 2022 Status: {(data.revenue_2022 || 0) > (data.revenue_2019 || 0) ? 'Exceeded' : 'Below'} pre-pandemic levels</li>
        </ul>
      </div>
    </div>
  );
}
